import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "../components/navbar";
import Footer from "../components/footer";

// Point this to your backend (Railway/FastAPI)
const API_BASE = process.env.REACT_APP_API_BASE;

const AuthPage = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", password: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isLogin, setIsLogin] = useState(true); // Toggle between Login and Signup

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let endpoint = `${API_BASE}/auth/login`;
      const method = "POST";

      if (!isLogin) {
        // Handle signup logic
        if (form.password !== form.confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        endpoint = `${API_BASE}/auth/signup`; // Assume you have a signup endpoint
      }

      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, password: form.password }),
        credentials: "omit",
      });

      if (!res.ok) {
        const msg = await res.json().catch(() => ({}));
        throw new Error(msg.detail || t("common.error_occurred"));
      }

      const data = await res.json(); // expect: { access_token, token_type, role }
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("role", data.role || "visitor");
      localStorage.setItem("email", form.email);

      // Redirect or handle successful login/signup
      window.location.href = "/";
    } catch (err) {
      setError(err.message || t("common.operation_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      <Navbar />

      <div className="flex-grow flex items-center justify-center px-6 py-20">
        <div className="max-w-md w-full bg-white p-10 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
          <h2 className="text-3xl font-semibold text-center text-gray-800 mb-6">
            {isLogin ? t("login.title") : t("signup.title")}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-600 mb-1" htmlFor="email">
                {t("login.email")}
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="username"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1" htmlFor="password">
                {t("login.password")}
              </label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              />
            </div>

            {!isLogin && (
              <div>
                <label className="block text-gray-600 mb-1" htmlFor="confirmPassword">
                  {t("signup.confirmPassword")}
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                />
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded p-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full text-white py-3 rounded-lg font-semibold transition-all ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
              }`}
            >
              {loading ? (isLogin ? t("common.signing_in") : t("common.creating_account")) : isLogin ? t("login.button") : t("signup.button")}
            </button>
          </form>

          <p className="mt-6 text-sm text-gray-500 text-center">
            {isLogin ? t("login.noAccount") : t("signup.haveAccount")}{" "}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-cyan-600 hover:text-cyan-800 font-medium"
            >
              {isLogin ? t("login.signup") : t("signup.login")}
            </button>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AuthPage;
